import { useEffect, useState, useRef, FormEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";
import { useParams, useNavigate } from "react-router-dom";

import InputGroup from "./InputGroup";
import Container from "components/__reusable/Container";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import FormActionBar from "components/__reusable/FormActionBar";
import MessageDialog from "components/__reusable/MessageDialog";
import RememberPasswordPromptDialog from "./RememberPasswordPrompt";
import LoadingDialog from "components/__reusable/LoadingDialog";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import {
  getTeachers,
  unlockTeacherAccount,
  updateTeacher,
} from "features/teacher/teacherAction";
import { generate } from "utilities/RandomStringGenerator";
import type {
  TeacherFormData,
  UnlockTeacherAccountFormData,
} from "types/Teacher";
import type { DropdownInputOption } from "types/DropdownInputOption";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";
import { DELETE_ACCOUNT_PATH } from "router/pathName";

const UpdateForm = () => {
  const { t } = useTranslation();
  const [teacher, setTeacher] = useState<TeacherFormData>({
    first_name: "",
    last_name: "",
    first_name_kata: "",
    last_name_kata: "",
    login_key: "",
    staff_class: [],
  });
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [selectedClassOptions, setSelectedClassOptions] = useState<
    DropdownInputOption[]
  >([]);

  const { teachers, loading } = useAppSelector((state) => state.teacher);
  const dispatch = useAppDispatch();
  const fetchTeachers = () => teachers.length === 0 && dispatch(getTeachers());
  const callFetchTeachers = useCallback(fetchTeachers, [dispatch, getTeachers]);
  useEffect(() => {
    callFetchTeachers();
  }, [callFetchTeachers]);

  const { id } = useParams();
  useEffect(() => {
    if (isNaN(parseInt(id || ""))) return;
    const teacher = teachers.find(
      (teacher) => teacher.id === parseInt(id || "")
    );
    if (!teacher) return;

    setSelectedClassOptions(
      teacher.staff_class.map((classItem) => ({
        label: `${classItem.year} - ${classItem.name}`,
        value: classItem.id.toString(),
      }))
    );
    setTeacher({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
      first_name_kata: teacher.first_name_kata,
      last_name_kata: teacher.last_name_kata,
      login_key: teacher.login_key,
      staff_class: teacher.staff_class.map((c) => c.id.toString()),
    });
    // Set locked_until from teacher data (assuming it exists on the teacher object)
    setLockedUntil(
      teacher.locked_until ? new Date(teacher.locked_until) : null
    );
  }, [id, teachers]);

  // Helper function to check if account is currently locked
  const isAccountLocked = () => {
    if (!lockedUntil) return false;
    return lockedUntil > new Date();
  };

  const [isDisable, setIsDisable] = useState(true);

  const [isShowResetPasswordPrompt, setIsShowResetPasswordPrompt] =
    useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const resetPassword = () => {
    setIsShowResetPasswordPrompt(false);
    setIsResetPassword(true);
    setTeacher({ ...teacher, password: generate(8) });
  };

  const formElement = useRef<HTMLFormElement>(null);
  const update = async (e: FormEvent | undefined) => {
    if (e) e.preventDefault();
    setIsRememberPassword(true);
    if (!formElement.current) return;
    const formData = new FormData(formElement.current);
    const teacherFormData: TeacherFormData = {
      first_name: formData.get("first_name") as string,
      last_name: formData.get("last_name") as string,
      first_name_kata: formData.get("first_name_kata") as string,
      last_name_kata: formData.get("last_name_kata") as string,
      login_key: formData.get("login_key") as string,
      staff_class: (formData.get("staff_class") as string).split(","),
      password: (formData.get("password") as string) || undefined,
    };
    const updateAction = await dispatch(
      updateTeacher({ id: parseInt(id || ""), formData: teacherFormData })
    );
    if (isFulfilled(updateAction)) {
      dispatch(
        setSuccessStatus({
          title: "_account_form._update_success",
          message: "_account_form._update_success_content",
        })
      );
      setIsResetPassword(false);
    } else {
      dispatch(setErrorStatus(""));
    }
  };

  const [isRememberPassword, setIsRememberPassword] = useState(true);
  const showRememeberPasswordPrompt = (e: FormEvent) => {
    e.preventDefault();
    setIsRememberPassword(false);
  };

  const unlockAccount = async () => {
    const formData: UnlockTeacherAccountFormData = {
      locked_until: null, // Set locked_until to null to unlock the account
    };
    const unlockAction = await dispatch(
      unlockTeacherAccount({ id: parseInt(id || ""), formData })
    );
    if (isFulfilled(unlockAction)) {
      // Update local state to reflect the unlock
      setLockedUntil(null);
    } else {
      dispatch(setErrorStatus(""));
    }
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-3">
      <Container>
        <form
          ref={formElement}
          className="w-full lg:w-3/4 space-y-3 lg:space-y-5"
          onSubmit={(e) =>
            isResetPassword ? showRememeberPasswordPrompt(e) : update(e)
          }
        >
          <InputGroup
            teacher={teacher}
            selectedClassOptions={selectedClassOptions}
            disableSubmit={(isDisable) => setIsDisable(isDisable)}
            passwordAction={
              <SecondaryActionButton
                label={t("_account_form._new_password")}
                disabled={isResetPassword}
                onClick={() => setIsShowResetPasswordPrompt(true)}
              />
            }
          />
          <div className="lg:flex items-center space-y-3 lg:space-x-3 lg:space-y-0">
            <h3 className="lg:w-1/4 font-medium">アカウント状態</h3>
            <div className="w-full flex items-center space-x-5">
              <div className="flex flex-col">
                <p className="font-medium">
                  {isAccountLocked() ? "ロック" : "有効"}
                </p>
                {isAccountLocked() && lockedUntil && (
                  <p className="text-sm text-gray-600">
                    ロック解除: {lockedUntil.toLocaleString()}
                  </p>
                )}
              </div>
              {isAccountLocked() && (
                <div className="w-[10rem]">
                  <PrimaryActionButton
                    isButton={true}
                    label={"ロックを解除する"}
                    disabled={isDisable}
                    onClick={unlockAccount}
                  />
                </div>
              )}
            </div>
          </div>
          <FormActionBar
            primaryLabel={t("_account_form._submit")}
            primaryDisabled={isDisable}
            primaryLoading={loading}
          />
          {loading && <LoadingDialog />}
        </form>
      </Container>
      <div className="px-3 sm:px-5">
        <button
          type="button"
          className="font-semibold text-[#5B5B5B] transition-colors duration-200 ease-out hover:text-[#ff0000]"
          onClick={() => navigate(`${DELETE_ACCOUNT_PATH}/${id}`)}
        >
          {t("_account_form._delete_teacher_button")}
        </button>
      </div>
      {isShowResetPasswordPrompt && (
        <MessageDialog>
          <div className="space-y-3">
            <p className="text-center">
              {t("_account_form._new_password_warning")}
            </p>
            <div className="flex space-x-5">
              <div className="w-1/2">
                <SecondaryActionButton
                  label={t("_generic._no")}
                  onClick={() => setIsShowResetPasswordPrompt(false)}
                />
              </div>
              <div className="w-1/2">
                <PrimaryActionButton
                  label={t("_generic._yes")}
                  onClick={resetPassword}
                />
              </div>
            </div>
          </div>
        </MessageDialog>
      )}
      {!isRememberPassword && (
        <RememberPasswordPromptDialog
          onNoClick={() => setIsRememberPassword(true)}
          onYesClick={() => update(undefined)}
        />
      )}
    </div>
  );
};

export default UpdateForm;
